'use client';

import React, { createContext, useContext } from 'react';
import type { Vehicle } from '@/types';

/**
 * Data the published storefront and the builder canvas share with sections.
 *
 * `vehiclesReady` lets dynamic sections show a skeleton until the tenant
 * catalogue has been fetched. Defaults are empty so production never paints
 * demo vehicles before the real catalogue arrives.
 */
export interface StorefrontAgencyContact {
  phone?: string;
  email?: string;
  whatsapp?: string;
  address?: string;
  hours?: string;
  name?: string;
  logoUrl?: string;
}

export interface StorefrontData {
  vehicles: Vehicle[];
  vehiclesReady: boolean;
  /** True inside the builder canvas and the section-library preview. */
  preview?: boolean;
  /** Live agency contact used when section fields are empty. */
  agency?: StorefrontAgencyContact;
}

const DEFAULT_DATA: StorefrontData = {
  vehicles: [],
  vehiclesReady: false,
  preview: true,
  agency: {},
};

const StorefrontDataContext = createContext<StorefrontData>(DEFAULT_DATA);

export function StorefrontDataProvider({
  value,
  children,
}: {
  value: StorefrontData;
  children: React.ReactNode;
}) {
  return (
    <StorefrontDataContext.Provider value={value}>
      {children}
    </StorefrontDataContext.Provider>
  );
}

export function useStorefrontData(): StorefrontData {
  return useContext(StorefrontDataContext);
}

const DEMO_PLACEHOLDERS = new Set([
  'drivex',
  'drive x',
  'contact@drivex.ma',
  '+212528840000',
  '+212 528 84 00 00',
  '+212 528 840 000',
  '+212 600 000 000',
  'av. hassan ii, agadir 80000, morocco',
  'agadir, morocco',
]);

function isDemoPlaceholder(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (DEMO_PLACEHOLDERS.has(normalized)) return true;
  if (normalized.startsWith('© 2026 drivex')) return true;
  if (normalized.includes('drivex.ma')) return true;
  return false;
}

/** Prefer section content, then agency profile, then empty. Demo placeholders count as empty. */
export function resolveContactField(
  contentValue: unknown,
  agencyValue: string | undefined,
): string {
  if (typeof contentValue === 'string' && contentValue.trim() !== '' && !isDemoPlaceholder(contentValue)) {
    return contentValue.trim();
  }
  return typeof agencyValue === 'string' ? agencyValue.trim() : '';
}
